import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { EditAimMapViewServer, getFeatureLayerByLayerName, StaticMap, USAExtent } from '@repo/map';
import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {
  DeviceType,
  errorBoundaryWrapper,
  useAsyncEffect,
  useDeviceDetection,
  useLocalStorageState
} from '@repo/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import PopupMap from './PopupMap';
import { Button, Modal, Overlay } from '@mantine/core';
import Extent from '@arcgis/core/geometry/Extent';
import FullHeightLoadingOverlay from '../../FullHeightLoadingOverlay';
import { useDisclosure } from '@mantine/hooks';
import './OpportunityAcquisitionMap.css';
import LaunchIcon from 'calcite-ui-icons-react/LaunchIcon';
import { IconArrowsMaximize } from '@tabler/icons-react';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import { useGetPrismTransactionsQuery } from '@repo/services';

// const baseUrl = process.env.PUBLIC_URL as string;

const OpportunityAcquisitionMap = ({ topRightButton = false }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const { id } = useParams();
  const location = useLocation();
  const {
    currentData: transactions = [],
    isLoading: transactionsLoading,
    isSuccess: transactionsSuccess,
    isError
  } = useGetPrismTransactionsQuery(null);
  const device = useDeviceDetection();
  const isIpad = useMemo(() => device === DeviceType.tablet, [device]);
  const opportunityId = useMemo(() => {
    if (id && parseInt(id)) {
      return transactions?.find((transaction) => transaction.id === +id)?.id || +id;
    }
    return 0;
  },[id, transactions]);
  const navigate = useNavigate();
  const [view, setView] = useState<MapView>();
  const [featureLayer, setFeatureLayer] = useState<FeatureLayer>();
  const [notFound, setNotFound] = useState(true);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opportunityExtent, setOpportunityExtent] = useLocalStorageState(
    `opportunityExtent-${opportunityId}`,
    { defaultValue: USAExtent } as any
  );
  const onLoad = (view: MapView) => {
    //@ts-ignore
    view.extent = new Extent(opportunityExtent);
    setView(view);
  };
  const foundAndZoomToExtent = async (extent: Extent) => {
    setNotFound(false);
    const zoomExtent: Extent = extent.expand(1);
    // const zoomExtent: Extent = extent.expand(3);
    setOpportunityExtent(zoomExtent.toJSON());
    // featureLayer && featureLayer.maxScale = 0;
    if (featureLayer) featureLayer.definitionExpression = '';
    await view?.goTo(zoomExtent);
  };

  useEffect(() => {
    if (isPoppedOut) { open(); } else { close(); }
  },[isPoppedOut])

  useAsyncEffect(async () => {
    if (opportunityId && view && !featureLayer) {
      // setLoading(true);
      const featureLayer = await getFeatureLayerByLayerName({
        ...EditAimMapViewServer,
        layerName: 'Potential Acquisitions',
        definitionExpression: `OpportunityID=${opportunityId}`,
        maxScale: 0,
        minScale: 300000000
      });
      const renderer = new SimpleRenderer({
        symbol: {
          type: "simple-fill", // For polygons
          color: [255, 255, 255, 0], // Transparent fill
          outline: {
            color: [255, 255, 255, 1], // Red outline
            width: 2
          }
        }
      });

      featureLayer.renderer = renderer;
      featureLayer.labelingInfo = [
        {
          labelExpressionInfo: {
            expression: "$feature.NAME"
          },
          symbol: {
            type: "text",
            color: "black",
            haloColor: "white",
            haloSize: "10px",
            font: {
              size: 12,
              family: "Arial",
              weight: "bold"
            }
          },
          labelPlacement: "center-center"
        }
      ];

      featureLayer.labelsVisible = true;

      const { count, extent } = await featureLayer.queryExtent();
      view.map?.add(featureLayer);
      featureLayer.on('layerview-create', async () => {
        // const layer = event.layerView.layer as FeatureLayer;
        if (count > 0) {
          await foundAndZoomToExtent(extent);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      });
      setFeatureLayer(featureLayer);
      // view.when(() => setLoading(false));
    }
    if (opportunityId && featureLayer) {
      const expression = `OpportunityID=${opportunityId}`;
      if (featureLayer.definitionExpression !== expression) {
        featureLayer.definitionExpression = expression;
        const { count, extent } = await featureLayer.queryExtent();
        if (count > 0) {
          await foundAndZoomToExtent(extent);
        } else {
          await view?.goTo(new Extent(USAExtent));
          setNotFound(true);
        }
      }
    }
  }, [opportunityId, view]);

  if (location.pathname.includes('/map')) {
    return (
      <div style={{ height: 'calc(100% - 64px)', width: '100%' }}>
        <Button onClick={() => window.close()}>Close</Button>
        <PopupMap opportunityId={opportunityId} />
      </div>
    );
  }

  const MapIcon = () => {
    return (
        <div>
          <LaunchIcon size={64} style={{ color: 'white' }} />
        </div>
    )
  }

  const handleClose = () => {
    setIsPoppedOut(false);
    close();
  }

  return (
    <>
      <FullHeightLoadingOverlay visible={loading} />
      <div
        style={{ cursor: notFound ? '' : 'pointer', minHeight: '30vh', height: '100%', width: '100%', position: 'relative' }}
        onClick={topRightButton ? undefined : () => {
          if (!notFound) {
            if (isIpad) {
              window.open(`/map/${opportunityId}`, '_blank');
            } else setIsPoppedOut(true);
            // else window.open(`${opportunityId}/map`, '_blank');
          }
        }}
      >
        <StaticMap onLoad={onLoad} />
        {notFound ? (
          <Overlay
              style={{
                userSelect: 'none',
                zIndex: 99
              }}
              className='notYetMapped'
          >
            This property has not yet been mapped.
          </Overlay>
        ) : (
            <Overlay
            className={topRightButton ? '' : 'opportunity-map'}
            backgroundOpacity={topRightButton ? 0 : undefined}
            color={topRightButton ? '' : 'rgba(1,1,1,0.42)'}
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: topRightButton ? 'flex-end' : 'center',
              alignItems: topRightButton ? 'flex-start' : 'center',
              zIndex: 99
            }}
          >
              {topRightButton ? (
                <Button
                  m={10}
                  color={'rgba(79,91,93,255)'}
                  onClick={() => {
                    if (!notFound) {
                      if (isIpad) {
                        window.open(`/map/${opportunityId}`, '_blank');
                      } else setIsPoppedOut(true);
                    }
                  }}
                >
                  <IconArrowsMaximize />
                </Button>
              ) : (
                <MapIcon />
              )}
          </Overlay>
        )}
      </div>
      <Modal opened={opened} onClose={handleClose} fullScreen
             radius={0}
             // classNames={{
             //   root: `${opened ? classes.rootOpen : classes.rootClosed}`,
             // }}
             // zIndex={opened ? 250 : 150}
             transitionProps={{ transition: 'fade', duration: 200 }}>
        <PopupMap opportunityId={opportunityId} />
        <>
          <Button
            // style={{ marginTop: 12, position: 'absolute', right: 165 }}
            onClick={() => {
              window.open(
                `/acquisition/${opportunityId}/map`,
                '_blank'
                // 'location=yes,height=570,width=520,scrollbars=yes,status=yes'
              );
              setIsPoppedOut(false);
            }}
          >
            Open in new window
          </Button>
          <Button onClick={() => setIsPoppedOut(false)}>Close</Button>
        </>
      </Modal>
      {/*<Button onClick={open}>Open</Button>*/}
    </>
  );
};

export default errorBoundaryWrapper(OpportunityAcquisitionMap);
